import { useCallback, useMemo, useState } from "react";

import { DataExtractionModal } from "components/Data-extraction/DataExtractionModal";
import { AiProcessingContent } from "components/modals/Files-categorization-modal/AiProcessedContent";
import {
  useActionHook,
  useDealRoleGuard,
  useModals,
  useNavigate,
  useNavigationHooks,
  usePathParam,
  usePrompt
} from "hooks";
import {
  ApiServiceError,
  useDealActions,
  useDealCSSelector,
  useDocumentsActions,
  useFolderSelect,
  useFoldersNumberSelect,
  useTaskManageActions,
  useTokenSelector,
  useUserActions,
  useUserIdSelector,
  useUserRoleSelector,
  useViewActions
} from "store";
import { FolderKind, ICapitalSeeker, IFile, IRole } from "store/types";
import { toastify } from "utils/tyostify";

import { FileType, RoleName } from "../../../constants";
import { Routes } from "../../../constants/routes";
import { isRoleAnalyst } from "../../../utils/user";
import { ContextMenuOptionObjectType } from "../../popups/Context-menu/Options-collection";
import { FileInfoPrompt } from "../../Prompts/FileInfoPrompt";

interface IUseFilePreviewHooks {
  openShareDialog: (() => void) | undefined;
  onDelete: (() => void) | undefined;
  onMoveTo: ContextMenuOptionObjectType | undefined;
  onDownload: () => void;
  onFileClick: () => void;
  onFileInfo: () => void;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  actionHandler: () => void;
  onAddValueFromPDF: undefined | (() => void);
  // onAnalyzeFileWithGPT: undefined | (() => void);
  onAddTask?: () => void;
  onSetPrivate?: () => void;
  isParentPrivate: boolean;
  downloadLoading: boolean;
  onExtractFileK1?: () => Promise<void>;
  onCategorize?: () => void;
  onDataExtraction?: () => void;
  onLookThrough?: () => void;
}

export function useFilePreviewHooks(file: IFile, folderKind?: FolderKind): IUseFilePreviewHooks {
  const {
    id: fileId,
    name: fileName,
    user_id: fileOwnerId,
    deal_id: dealId,
    section_id: sectionId,
    file_url,
    type,
    is_private,
    email_id: emailId
  } = file || {};
  // `${process.env.REACT_APP_PDF_READ_URL}/verifyToken?&server=${protocol}${domain}&deal=${dealId}`
  const { deleteFileModal, errorModal } = useModals();
  const openPrompt = usePrompt();
  const navigate = useNavigate();
  const { setParams, getFilePathName } = useNavigationHooks();
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);

  // const { addFiles } = useTaggerActions();
  const { setShareDialog } = useViewActions();
  const { setCreateAction } = useTaskManageActions();
  const { downloadEmailFileAction } = useUserActions();
  const { downloadDocumentFileAction } = useDocumentsActions();
  const {
    downloadFileAction,
    moveFileAction,
    getEmailsAction,
    updateFileAction,
    downloadFileFromDRAction,
    getFolderMetadataAction,
    dispatchFileAction,
    extractFileK1Action,
    createInvestmentDetailFromAIAction,
    getFileMetaAction
  } = useDealActions();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const token = useTokenSelector();
  const userId: number | undefined = useUserIdSelector();
  const role: IRole | null = useUserRoleSelector();
  const foldersLength: number = useFoldersNumberSelect();
  const folder = useFolderSelect(file?.section_id);
  const capitalSeekers = useDealCSSelector();
  const pathFolderId = usePathParam("folder");

  const capitalSeeker: ICapitalSeeker | undefined = capitalSeekers?.[0];

  const { action: getFileMeta } = useActionHook(getFileMetaAction);

  const { action: createInvestmentDetailFromAi } = useActionHook(createInvestmentDetailFromAIAction, {
    onSuccess: async () => {
      navigate({
        route: "dealLookThrough",
        params: { deal_id: dealId }
      });
    },
    onReject: (err) => {
      toastify({ message: err.message, type: "error" });
    }
  });

  const openShareDialog: (() => void) | undefined = useDealRoleGuard(
    () =>
      setShareDialog({
        elements: [fileId],
        type: "file_id",
        shareName: fileName,
        dealId: dealId,
        dealCapitalSeekerEmail: capitalSeeker?.email,
        itemToShare: file.is_private ? null : file
      }),
    [RoleName.Admin, RoleName.Analyst]
  );

  const onDelete: (() => void) | undefined = useMemo(() => {
    if (isRoleAnalyst({ role }) || fileOwnerId === userId) {
      return () => deleteFileModal(file);
    }
  }, [file, role, fileOwnerId, userId]);

  const onFileClick: () => void = useCallback(
    () =>
      setParams({
        search: { section: String(sectionId) },
        pathname: getFilePathName(dealId, fileId)
      }),
    [sectionId, dealId, fileId]
  );

  const onMoveTo: ContextMenuOptionObjectType | undefined = useDealRoleGuard(
    {
      action: async (sectionId: number) => {
        try {
          const updateFile = await moveFileAction({
            sectionId,
            file,
            useMethod: folderKind === "email" ? "add" : "update"
          });
          if (folderKind === "email") {
            getEmailsAction(dealId);
          }
          navigate({
            route: "dealFolder",
            params: { deal_id: updateFile.deal_id, folder_id: updateFile.section_id },
            search: { section: updateFile.section_id }
          });
        } catch (error) {
          console.log(error);
        }
      }
    },
    [RoleName.Admin, RoleName.Analyst],
    foldersLength < 2
  );

  const onDownload: () => void = useCallback(() => {
    if (dealId) {
      downloadFileAction({ fileId, fileName, dealId });
    } else if (emailId) {
      downloadEmailFileAction({ emailId: emailId, fileId });
    } else if (!dealId && file.document_id) {
      downloadDocumentFileAction({ documentId: file.document_id, fileId: file.id });
    }
  }, [fileId, fileName, dealId, emailId]);

  const actionHandler = async () => {
    if (file.type === "png" || file.type === "jpeg" || file.type === "jpg" || file.type === "pdf") {
      if (file.file_url === "") {
        if (downloadLoading) return;
        try {
          setDownloadLoading(true);
          //download file from DR
          const syncedFile = await downloadFileFromDRAction({ dealId, fileId: file.id });
          dispatchFileAction(syncedFile);
          if (pathFolderId === file.section_id) {
            await getFolderMetadataAction({ dealId, sectionId: file.section_id, filters: {} });
          }
        } finally {
          setDownloadLoading(false);
        }

        if (file.type === "png" || file.type === "jpeg" || file.type === "jpg" || file.type === "pdf") {
          return;
        } else if (dealId) onFileClick();
      }
      setIsOpen(true);
    }
  };

  const onFileInfo = useCallback(() => {
    getFileMeta(dealId, fileId);
    openPrompt({
      Component: FileInfoPrompt,
      props: {
        file,
        onShare: openShareDialog,
        onDownload
      }
    });
  }, [file, openShareDialog, openPrompt]);

  const onAddValueFromPDF: (() => void) | undefined = useMemo(() => {
    if (token && type === FileType["PDF"]) {
      return async () => {
        try {
          setParams({
            search: {
              dealId,
              filesIds: [fileId],
              section: sectionId
            },
            pathname: Routes.tagger,
            usePush: true
          });
        } catch (error) {
          throw error;
        }
      };
    }
  }, [token, dealId, fileName, file_url, type, fileId]);

  // const onAnalyzeFileWithGPT = useCallback(async () => {
  //   if (file.file_url) {
  //     const res = await fetch(file.file_url);
  //     if (res.ok) {
  //       const blob = await res.blob();
  //       const documents = await addFiles(
  //         [{ file: new File([blob], file.name), dealId, folderId: file.section_id }],
  //         true
  //       );
  //
  //       if (documents.some((doc) => !doc.gpt_results)) {
  //         toast("File was sent for GPT analysis. You'll get a notification once the process is finished");
  //         setIsOpen(false);
  //       } else {
  //         setParams({
  //           search: { dealId, filesIds: documents.map((doc) => doc.id), taggerFile: 1, section: sectionId },
  //           pathname: Routes.tagger,
  //           usePush: true
  //         });
  //       }
  //     }
  //   } else {
  //     toast("File can not be processed");
  //   }
  // }, []);

  const onAddValueFromPDFWithRole: (() => void) | undefined = useDealRoleGuard(onAddValueFromPDF, [
    RoleName.Admin,
    RoleName.Analyst
  ]);

  // const onAnalyzeFileWithGPTRole: (() => void) | undefined = useDealRoleGuard(onAnalyzeFileWithGPT, [
  //   RoleName.Admin,
  //   RoleName.Analyst
  // ]);

  const onAddTask = useCallback(() => {
    setCreateAction({
      isOpen: true,
      entity_id: fileId,
      entity_type: "file"
    });
  }, [fileId]);

  const onAddTaskWithRole: (() => void) | undefined = useDealRoleGuard(onAddTask, [RoleName.Admin, RoleName.Analyst]);

  const onPrivate = useCallback(async () => {
    await updateFileAction({
      dealId,
      fileId,
      is_private: !is_private
    });
  }, [is_private, dealId, fileId]);

  const onSetPrivate: (() => void) | undefined = useDealRoleGuard(
    !folder?.is_private && file.user_id && file.user_id === userId ? onPrivate : undefined,
    [RoleName.Admin, RoleName.Analyst]
  );

  const onExtractFileK1 = useCallback(async () => {
    try {
      extractFileK1Action(file.id);
    } catch (e) {
      errorModal({ msg: (e as ApiServiceError).message });
    }
  }, [file]);

  const onExtractFileK1WithRole = useDealRoleGuard(onExtractFileK1, [RoleName.Admin, RoleName.Analyst]);

  const onCategorize: (() => void) | undefined = useMemo(() => {
    return () => {
      console.log("categorize");
    };
  }, [file]);

  const onDataExtraction = useCallback(async () => {
    await openPrompt({
      componentType: "fullscreen",
      Component: DataExtractionModal,
      props: {
        fileIds: [fileId]
      }
    });
  }, [fileId, openPrompt]);

  const isParentPrivate = Boolean(folder?.is_private);

  const onLookThrough = useCallback(async () => {
    try {
      await createInvestmentDetailFromAi({ file_ids: [fileId], files: [], dealId });
      await openPrompt({
        Component: AiProcessingContent,
        props: {
          title: "Upload file",
          type: "lookThrough",
          applyButtonText: "Continue"
        }
      });
    } catch (error) {
      console.log(error);
    }
  }, [fileId, dealId, openPrompt, AiProcessingContent]);

  return {
    openShareDialog,
    onDelete,
    onMoveTo,
    onDownload,
    onFileClick,
    onFileInfo,
    isOpen,
    setIsOpen,
    actionHandler,
    onAddValueFromPDF: onAddValueFromPDFWithRole,
    onAddTask: onAddTaskWithRole,
    onSetPrivate,
    isParentPrivate,
    downloadLoading,
    onExtractFileK1: onExtractFileK1WithRole,
    onCategorize,
    onDataExtraction,
    onLookThrough
  };
}
